import { LoadingRows } from "../../UI/Loading/LoadingRows.tsx";
import { Layout } from "../../UI/Layout/Layout.tsx";
import { ScreenTitleBlock } from "../../UI/Layout/ScreenTitleBlock.tsx";
import { Card } from "../../UI/Card.tsx";
import { useAllFranchisees } from "../useAllFranchisees.ts";
import { useState } from "react";
import { useLeaveTeacherCoordinatorReview } from "./useLeaveTeacherCoordinatorReview.ts";
import { FranchiseeItemFragment } from "../../gql/graphql.ts";
import { useNavigate } from "react-router-dom";
import { TeacherDetailScreenPath } from "../TeacherDetailScreen.tsx";
import { CoordinatorReviewScoreLegend } from "./CoordinatorReviewScoreLegend.tsx";
import { useTeacherCoordinatorReviews } from "./useTeacherCoordinatorReviews.ts";
import { teacherMissingReviewForPeriod } from "./coordinatorReviewPeriod.ts";

export const TeacherCoordinatorReviewScreenPath =
  "/teacher-coordinator-reviews";

type ScoreInputProps = {
  label: string;
  value: number;
  onChange: (v: number) => void;
};

function ScoreInput({ label, value, onChange }: ScoreInputProps) {
  return (
    <div className={"flex items-center gap-3"}>
      <span className={"text-sm text-slate-600 w-36"}>{label}</span>
      <div className={"flex gap-1"}>
        {[1, 2, 3, 4, 5].map((n) => (
          <button
            key={n}
            type={"button"}
            onClick={() => onChange(n)}
            className={[
              "w-9 h-9 rounded border text-sm font-medium transition-colors",
              value === n
                ? "bg-slate-800 text-white border-slate-800"
                : "bg-white text-slate-600 border-slate-300 hover:border-slate-500",
            ].join(" ")}
          >
            {n}
          </button>
        ))}
      </div>
    </div>
  );
}

type TeacherReviewFormProps = {
  teacher: FranchiseeItemFragment;
  onSuccess: () => void;
};

function TeacherReviewForm({ teacher, onSuccess }: TeacherReviewFormProps) {
  const [fluencyScore, setFluencyScore] = useState(0);
  const [presenceScore, setPresenceScore] = useState(0);
  const [clarityScore, setClarityScore] = useState(0);
  const { leaveReview, loading } = useLeaveTeacherCoordinatorReview();

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (fluencyScore === 0 || presenceScore === 0 || clarityScore === 0) return;
    await leaveReview({
      teacherId: teacher.id,
      fluencyScore,
      presenceScore,
      clarityScore,
    });
    onSuccess();
  }

  return (
    <form onSubmit={handleSubmit} className={"flex flex-col gap-3"}>
      <div
        className={"mb-2 p-3 bg-slate-50 rounded-lg border border-slate-200"}
      >
        <p
          className={
            "text-xs font-semibold text-slate-400 uppercase tracking-wide mb-3"
          }
        >
          Score reference
        </p>
        <CoordinatorReviewScoreLegend />
      </div>
      <ScoreInput
        label={"Fluency conditions"}
        value={fluencyScore}
        onChange={setFluencyScore}
      />
      <ScoreInput
        label={"Role adaptation"}
        value={presenceScore}
        onChange={setPresenceScore}
      />
      <ScoreInput
        label={"Clarity"}
        value={clarityScore}
        onChange={setClarityScore}
      />
      <div>
        <button
          type={"submit"}
          disabled={
            loading ||
            fluencyScore === 0 ||
            presenceScore === 0 ||
            clarityScore === 0
          }
          className={
            "px-4 py-2 bg-slate-800 text-white text-sm rounded disabled:opacity-50"
          }
        >
          {loading ? "Saving..." : "Save review"}
        </button>
      </div>
    </form>
  );
}

export function TeacherCoordinatorReviewScreen() {
  const { franchisees, loading: loadingFranchisees } = useAllFranchisees();
  const teacherIds = franchisees.map((t) => t.id);
  const {
    reviewByTeacherId,
    loading: loadingReviews,
    refetch: refetchReviews,
  } = useTeacherCoordinatorReviews(teacherIds);
  const navigate = useNavigate();

  const loading = loadingFranchisees || loadingReviews;

  function toDetailScreen(teacherId: string) {
    navigate(TeacherDetailScreenPath.replace(":teacherId", teacherId));
  }

  return (
    <Layout>
      <Card>
        <ScreenTitleBlock title={"Teacher Coordinator Reviews"} />
      </Card>
      <div className={"mt-4 flex flex-col gap-4"}>
        {loading ? (
          <LoadingRows rows={6} />
        ) : (
          franchisees.map((teacher) => {
            const latestReview = reviewByTeacherId.get(teacher.id);
            const needsReview = teacherMissingReviewForPeriod(
              teacher.createdAt,
              latestReview?.reviewedAtUtc,
            );
            return (
              <Card key={teacher.id}>
                <div className={"flex flex-col gap-4"}>
                  <div className={"flex items-center justify-between"}>
                    <div>
                      <p className={"font-semibold text-slate-800"}>
                        {teacher.givenName} {teacher.familyName}
                      </p>
                      <p className={"text-sm text-slate-500"}>
                        {teacher.email}
                      </p>
                    </div>
                    <div className={"flex items-center gap-3"}>
                      {!needsReview && latestReview && (
                        <span
                          className={
                            "text-sm text-green-600 font-medium bg-green-50 px-3 py-1 rounded-full"
                          }
                        >
                          Reviewed
                        </span>
                      )}
                      <button
                        onClick={() => toDetailScreen(teacher.id)}
                        className={
                          "text-sm text-slate-500 underline hover:text-slate-800"
                        }
                      >
                        View
                      </button>
                    </div>
                  </div>
                  {needsReview && (
                    <TeacherReviewForm
                      teacher={teacher}
                      onSuccess={refetchReviews}
                    />
                  )}
                </div>
              </Card>
            );
          })
        )}
      </div>
    </Layout>
  );
}
